import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppTooltip from '@tenant/core/AppTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntlMessages from '@tenant/utility/IntlMessages';
import useGoBack from '@tenant/utility/hooks/useGoBack';
import { ROUTE_STRING } from 'constants/RouteString';
import { Formik } from 'formik';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { LoadingButton } from '@mui/lab';
import validationSchema from '../../redux/slices/appInstance/constants/validationSchema';
import useAddAppInstanceInline from 'redux/slices/appInstance/hooks/useAddAppInstanceInline';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AddEnvironmentDrawer from './components/Add/AddEnvironmentDrawer';
import useModal from '@tenant/utility/hooks/useModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MarketplaceAppSelect from '@tenant/core/App/Select/MarketplaceAppSelect';
import DockerAppTypes from 'constants/DockerAppTypes';
import DockerImageCollapseCard from 'pages/appInstance/components/Add/DockerImageCollapseCard';
import AppsContainer from '@tenant/core/AppsContainer';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

// Package-free ("inline container") instance create form: no App Package is authored or
// selected here - containers are defined inline and the instance + every container persist a
// genuine NULL AppPackageId/AppComponentId (Wave C; see AppInstanceService.CreateInlineAsync).

const isCronDeploymentType = (deploymentType) =>
  DockerAppTypes.find((e) => e.Value === deploymentType)?.IsCron ?? false;

const findDuplicateNames = (containerApps = []) => {
  const seen = new Map();
  const duplicates = new Set();

  containerApps.forEach((row) => {
    const key = (row?.Name ?? '').trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) {
      duplicates.add(seen.get(key));
      duplicates.add(row.Name);
    } else {
      seen.set(key, row.Name);
    }
  });

  return Array.from(duplicates);
};

const Add = () => {
  const { visible, onClose, onShow } = useModal();
  const { 
    visible: confirmModalVisible, 
    onClose: onConfirmModalClose, 
    onShow: onConfirmModalShow 
  } = useModal();
  const [containerApp, setContainerApp] = useState();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [timeSubmitted, setTimeSubmitted] = useState(0);
  const [isValidContainerRows, setIsValidContainerRows] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const navigate = useNavigate();
  const goBack = useGoBack();
  const onClickBackButton = () => {
    goBack(ROUTE_STRING.APP_INSTANCE.LIST);
  };
  const addItem = useAddAppInstanceInline();

  const isSubmittable = (data) => {
    const containerApps = data?.ContainerApps ?? [];

    if (!containerApps.length) return false;
    if (!isValidContainerRows) return false;
    if (containerApps.some((e) => !e.isValid)) return false;
    if (findDuplicateNames(containerApps).length > 0) return false;

    return true;
  };

  const handleSubmit = (data) => {
    if (!isSubmittable(data)) {
      setSubmitAttempted(true);
      setTimeSubmitted((s) => s + 1);
      return;
    }

    // Store form data and show confirmation modal
    setPendingFormData(data);
    setIsAcknowledged(false);
    onConfirmModalShow();
  };

  const handleConfirmSubmit = () => {
    if (!isAcknowledged || !pendingFormData) return;

    const convertData = {
      Name: pendingFormData.Name,
      AppIds: pendingFormData?.AppModels?.map((e) => e.Id),
      ContainerApps: pendingFormData?.ContainerApps?.map((e) => ({
        Name: e.Name,
        ImageName: e.ImageNameItem?.Name,
        Cron: e.Cron ?? undefined,
        EnviromentVariables: (e.Environments ?? []).reduce(
          (acc, { key, value }) => {
            acc[key] = value;
            return acc;
          },
          {},
        ),
      })),
    };

    addItem.mutate(convertData, {
      onSuccess(result) {
        // `replace` — the instance and its containers are persisted, so this form is spent.
        // Leaving it on the stack makes back on the new detail page re-open a create form
        // for an instance that already exists.
        if (result?.data?.Id)
          navigate(
            ROUTE_STRING.APP_INSTANCE.VIEW.replace(':id', result?.data?.Id),
            { replace: true },
          );
      },
    });
    
    onConfirmModalClose();
  };

  const handleConfirmModalClose = () => {
    setIsAcknowledged(false);
    setPendingFormData(null);
    onConfirmModalClose();
  };

  const handleShow = (item) => {
    setContainerApp({
      ...item,
    });
    onShow();
  };

  const handleClose = () => {
    setContainerApp(null);
    onClose();
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Container App Name',
      width: 250,
      sortable: true,
      flex: 1,
    },
    {
      field: 'Image',
      headerName: 'Container Image',
      width: 250,
      sortable: true,
      flex: 1,
      renderCell: (params) => params.row.ImageNameItem?.Name ?? '',
    },
    {
      field: 'isValid',
      headerName: '',
      width: 100,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <>
          {params.value ? (
            <Tooltip title='Valid'>
              <CheckCircleIcon color='success' />
            </Tooltip>
          ) : (
            <Tooltip title='Invalid'>
              <CancelIcon color='error' />
            </Tooltip>
          )}
        </>
      ),
    },
    {
      field: 'Action',
      headerName: 'Environments',
      width: 125,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <IconButton onClick={() => handleShow(params.row)}>
          <AddIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Library</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.APP_INSTANCE.LIST}>App Instances</Link>
          </Typography>
          <Typography color='text.primary'>Add</Typography>
        </Breadcrumbs>
      }
    >
      <AppsHeader>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 4,
            }}
            component='span'
            mr={{ xs: 2, sm: 4 }}
          >
            <AppTooltip title={<IntlMessages id='common.back' />}>
              <ArrowBackIcon
                sx={{
                  color: 'text.secondary',
                }}
                onClick={onClickBackButton}
              />
            </AppTooltip>
          </Box>
        </Box>
      </AppsHeader>

      <Box p={4}>
        <Formik
          enableReinitialize
          validateOnChange={true}
          validateOnBlur={true}
          initialValues={{
            ContainerApps: [],
            DomainPrefix: 'lan-test',
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({
            submitForm,
            values,
            touched,
            setFieldValue,
            errors,
            setFieldTouched,
          }) => {
            const duplicateNames = findDuplicateNames(values.ContainerApps);

            const handleContainerAppsChange = (
              newRows,
              isValidNamesAndImages,
            ) => {
              const merged = newRows.map((row) => {
                const existing = values.ContainerApps?.find(
                  (c) => c.id === row.id,
                );
                const hasImage = !!row.ImageNameItem;
                const isCron = isCronDeploymentType(
                  row.ImageNameItem?.DeploymentType,
                );

                return {
                  ...row,
                  Environments: existing?.Environments ?? [],
                  Cron: existing?.Cron,
                  // Cron (Scheduled Job) rows must be explicitly configured via the
                  // Environments drawer (a Cron expression is required) before they are
                  // considered valid; Web rows are valid as soon as Name + Image are set.
                  isValid:
                    hasImage && (isCron ? existing?.isValid ?? false : true),
                };
              });

              setFieldValue('ContainerApps', merged);
              setIsValidContainerRows(
                merged.length > 0 && isValidNamesAndImages,
              );
            };

            return (
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    p: 4,
                    ml: -4,
                    mr: -4,
                  }}
                >
                  <Grid container spacing={5}>
                    <Grid item xs={12} sm={12}>
                      <Box
                        component='p'
                        color='text.primary'
                        fontSize={14}
                        mb={2}
                      >
                        Name
                        <Box
                          component='span'
                          ml={1}
                          sx={{ color: 'error.main' }}
                        >
                          *
                        </Box>
                      </Box>
                      <TextField
                        fullWidth
                        value={values.Name ?? ''}
                        error={touched.Name && !!errors.Name}
                        helperText={touched.Name ? errors.Name : ''}
                        placeholder='E.g. joblogic-app-instance'
                        onChange={(e) => {
                          setFieldTouched('Name', true);
                          setFieldValue('Name', e.target.value, true);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        component='p'
                        color='text.primary'
                        fontSize={14}
                        mb={2}
                      >
                        Marketplace Apps
                        <Box
                          component='span'
                          ml={1}
                          sx={{ color: 'error.main' }}
                        >
                          *
                        </Box>
                      </Box>
                      <MarketplaceAppSelect
                        isMultiple
                        value={values.AppModels ?? []}
                        error={!!errors.AppModels}
                        onChange={(e, value) => {
                          setFieldValue('AppModels', value);
                          setFieldValue('ContainerApps', []);
                          setIsValidContainerRows(false);
                        }}
                      />
                    </Grid>
                  </Grid>

                  {!values?.AppModels?.length ? (
                    <Box mt={5}>
                      <Alert severity='info'>
                        Select at least one Marketplace App to choose from its
                        registered container images.
                      </Alert>
                    </Box>
                  ) : (
                    <>
                      <Box
                        component='p'
                        color='text.primary'
                        fontSize={14}
                        mt={5}
                        mb={2}
                      >
                        Container Apps
                        <Box
                          component='span'
                          ml={1}
                          sx={{ color: 'error.main' }}
                        >
                          *
                        </Box>
                      </Box>

                      <DockerImageCollapseCard
                        key={values.AppModels?.[0]?.Id}
                        appId={values.AppModels?.[0]?.Id}
                        timeSubmitted={timeSubmitted}
                        initialValues={
                          values.ContainerApps?.length > 0
                            ? values.ContainerApps
                            : [
                                {
                                  id: 'new-item-id-default',
                                  Name: '',
                                },
                              ]
                        }
                        handleChange={handleContainerAppsChange}
                      />

                      {submitAttempted && duplicateNames.length > 0 && (
                        <Alert severity='error' sx={{ mb: 3 }}>
                          Duplicate container Name(s):{' '}
                          {duplicateNames.join(', ')}. Each container Name
                          must be unique.
                        </Alert>
                      )}

                      {values?.ContainerApps?.length > 0 && (
                        <Box style={{ height: 'auto', width: '100%' }}>
                          <DataGrid
                            hideFooter
                            autoHeight
                            disableColumnSelector
                            disableColumnMenu
                            rows={values?.ContainerApps ?? []}
                            columns={columns}
                            getRowId={(row) => row.id}
                          />
                        </Box>
                      )}
                    </>
                  )}

                  {!!containerApp && (
                    <AddEnvironmentDrawer
                      visible={visible}
                      handleClose={handleClose}
                      title={containerApp.Name}
                      appType={containerApp.ImageNameItem?.DeploymentType}
                      requiredEnviromentVariables={
                        containerApp.Environments?.map((e) => ({
                          ...e,
                          isValid: e.isValid ?? false,
                          isRequired: e.isRequired ?? false,
                        })) ?? []
                      }
                      setFieldValue={setFieldValue}
                      cron={containerApp?.Cron}
                      setContainerApps={({ environments, cron, isValid }) => {
                        const newContainerApps = values?.ContainerApps?.map(
                          (e) =>
                            e.id === containerApp?.id
                              ? {
                                  ...e,
                                  Environments: environments,
                                  Cron: cron,
                                  isValid: !!e.ImageNameItem && isValid,
                                }
                              : e,
                        );

                        setFieldValue('ContainerApps', newContainerApps);
                      }}
                    />
                  )}
                </Box>

                <FooterWrapper
                  className='footer'
                  sx={{
                    margin: '20px -20px -16px -20px',
                    backgroundColor: '#f4f7fe',
                    height: '50px',
                  }}
                >
                  <Stack
                    direction='row'
                    justifyContent='end'
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      height: '100%',
                      p: 2,
                    }}
                  >
                    <Button
                      variant='contained'
                      color='secondary'
                      disabled={addItem.isPending}
                      onClick={onClickBackButton}
                      sx={{ mr: 3, minWidth: 80 }}
                    >
                      Cancel
                    </Button>

                    <LoadingButton
                      variant='contained'
                      loading={addItem.isPending}
                      sx={{ mr: 3, minWidth: 80 }}
                      onClick={() => {
                        submitForm();
                      }}
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                </FooterWrapper>
              </Box>
            );
          }}
        </Formik>
      </Box>

      {/* Deployment Guidelines Confirmation Modal */}
      <Dialog
        open={confirmModalVisible}
        onClose={handleConfirmModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="h5" component="div" fontWeight="bold" color="text.primary">
              Critical Deployment Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mandatory compliance for production deployments
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4 }}>
          <Box 
            sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: 'error.light', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.main'
            }}
          >
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'error.contrastText', fontWeight: 600 }}>
              IMPORTANT: The following deployment requirements are mandatory for all Container App deployments. Non-compliance may result in security vulnerabilities, performance issues, or deployment failures.
            </Typography>
          </Box>
          
          <Stack spacing={4}>
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  1
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'primary.main' }}>
                    Security Isolation: Separate Webhook Listeners from Frontend APIs
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Webhook listeners must be deployed in dedicated Container Apps, isolated from APIs serving frontend clients. This separation is mandatory to implement proper security configurations including IP whitelisting and authentication controls, which cannot be effectively applied in shared container environments.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'warning.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  2
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'warning.main' }}>
                    Background Processing: Container App Jobs Required
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    All scheduled, timed, or background processing workloads must be deployed as Container App Jobs. Standard Container Apps are designed for HTTP-triggered scaling and will terminate during idle periods, which will cause critical failures in background operations and scheduled tasks.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  3
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'success.main' }}>
                    Naming Standards: Organizational Compliance Required
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    All Container Apps and related resources must adhere to established organizational naming conventions. Use clear, descriptive, and consistent names to ensure operational maintainability, compliance auditing, and effective resource management across the enterprise environment.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'warning.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  4
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'warning.main' }}>
                    Performance Optimization: Concurrency Configuration
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Applications handling lightweight or high-frequency requests must have properly configured concurrency limits. For frontend APIs and similar workloads, configure maximum concurrent request limits appropriately (typically 500-1000) to ensure optimal resource utilization and system performance under load.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  5
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'error.main' }}>
                    Resource Management: Immediate Cleanup Required
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Temporary, test, or unused resources must be deleted immediately upon completion of evaluation activities. Failure to remove unused resources will result in unnecessary operational costs, environmental pollution, and potential resource conflicts that may impact production systems.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'info.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  6
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'info.main' }}>
                    API Access Policy: Backend Proxy Mandatory
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Direct frontend access to Marketplace Internal APIs is strictly prohibited and will be blocked. All frontend applications must route requests through designated backend services that act as secure proxies. This policy will be enforced through IP-based access restrictions effective immediately.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
          
          <Box 
            sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid', 
              borderColor: 'divider'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAcknowledged}
                  onChange={(e) => setIsAcknowledged(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  I acknowledge and agree to comply with all deployment requirements listed above
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleConfirmModalClose}
            variant="outlined"
            color="primary"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={!isAcknowledged}
            loading={addItem.isPending}
            size="large"
            sx={{ minWidth: 160 }}
          >
            Confirm & Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </AppsContainer>
  );
};

export default Add;
